/**
 * Slug-aware, multi-tenant sign-in.
 *
 * Step 1 resolves the team code to a tenant (`GET /signup/v1/team-lookup`)
 * because `POST /auth/v1/token` is tenant-scoped: the same email can exist in
 * more than one program. Step 2 takes credentials (or a brokered SSO provider).
 * Step 3 collects the second factor when the persona requires it. On success
 * the post-auth router (`/post-auth`) picks the persona's landing view.
 *
 * Deep links vs. bare `/login`:
 *   - `/login?team=slug` (portal address, signup success, "Forgot password"
 *     back-links) resolves the slug on mount and, when it is a real team,
 *     skips straight to credentials behind a team confirmation chip.
 *   - Bare `/login` starts with an EMPTY team code. The last team used on
 *     this device is offered as a one-click suggestion, never silently
 *     prefilled — so a staff deep link and a remembered team never look alike.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { TextField } from '../../components/text-field';
import type { TeamLookup } from '../../lib/api-client';
import { ApiError, SESSION_EXPIRED_MESSAGE, toUserMessage } from '../../lib/api-error';
import { ROUTES } from '../../lib/constants';
import { loadLastTeamSlug, saveLastTeamSlug } from '../../lib/session-storage';

import { LoginMfaStep } from './login-mfa';
import { validateEmail, validateSlug } from './signup-validation';
import { TeamChip, toTeamLookupMessage } from './team-chip';

/** Brokered providers offered to staff. Consumer providers are Fanatic-only. */
const SSO_PROVIDERS = [
  { id: 'google', label: 'Continue with Google Workspace' },
  { id: 'microsoft', label: 'Continue with Microsoft Entra ID' },
  { id: 'saml', label: 'School or district SSO (SAML 2.0)' },
] as const;

/** `resolving` is the on-mount lookup of a deep-linked `?team=` slug. */
type Stage = 'resolving' | 'team' | 'credentials' | 'mfa';

export function LoginPage(): JSX.Element {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startSession, isAuthenticated, isMfaPending, session } = useSession();

  // Only a deep link populates the team code. A remembered team is offered as
  // an explicit suggestion below the field instead (see `rememberedSlug`).
  const linkedSlug = (searchParams.get('team') ?? '').trim().toLowerCase();
  const [slug, setSlug] = useState(linkedSlug);
  const [rememberedSlug] = useState(() => (linkedSlug === '' ? loadLastTeamSlug() : ''));
  const [team, setTeam] = useState<TeamLookup | null>(null);
  const [stage, setStage] = useState<Stage>(linkedSlug === '' ? 'team' : 'resolving');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEnrolled, setIsEnrolled] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const nextPath = searchParams.get('next');

  // Already signed in (and past MFA): don't show the form again.
  useEffect(() => {
    if (isAuthenticated && !isMfaPending) navigate(nextPath ?? ROUTES.postAuth, { replace: true });
  }, [isAuthenticated, isMfaPending, navigate, nextPath]);

  // A PARTIAL session (password accepted, factor still outstanding) that lands
  // back here — the route guard bounces `/app/{slug}` for it — resumes at the
  // MFA step for its team instead of asking for the password again. The
  // partial token cannot reach anything else, so this is the only useful
  // place for it to be.
  useEffect(() => {
    if (!isAuthenticated || !isMfaPending || stage === 'mfa') return;
    if (team === null || session === null) return;
    if (session.tenantSlug !== null && session.tenantSlug !== team.slug) return;
    setIsEnrolled(session.isMfaEnrolled);
    setFormError(null);
    setStage('mfa');
  }, [isAuthenticated, isMfaPending, stage, team, session]);

  // The MFA card runs on the partial token from the password step. If that
  // session disappears underneath it (expired, revoked, signed out in another
  // tab) the card would be verifying against nothing — send the user back to
  // the password step with the team still selected, and say why.
  useEffect(() => {
    if (stage !== 'mfa' || isAuthenticated) return;
    setStage(team === null ? 'team' : 'credentials');
    setFormError(SESSION_EXPIRED_MESSAGE);
  }, [stage, isAuthenticated, team]);

  // Deep-linked `?team=`: confirm the slug is a real team and skip the
  // team-entry step. On failure fall back to the team form with the slug
  // still in the field and the error next to it.
  useEffect(() => {
    if (linkedSlug === '') return;
    let isCancelled = false;
    void (async () => {
      const slugError = validateSlug(linkedSlug);
      if (slugError !== null) {
        if (!isCancelled) {
          setFieldErrors({ slug: slugError });
          setStage('team');
        }
        return;
      }
      try {
        const found = await apiClient.lookupTeam(linkedSlug);
        if (isCancelled) return;
        setTeam(found);
        setSlug(found.slug);
        saveLastTeamSlug(found.slug);
        setStage('credentials');
      } catch (error) {
        if (isCancelled) return;
        setFieldErrors({ slug: toTeamLookupMessage(error, linkedSlug) });
        setStage('team');
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [apiClient, linkedSlug]);

  function applyTeam(found: TeamLookup): void {
    setTeam(found);
    setSlug(found.slug);
    saveLastTeamSlug(found.slug);
    setFieldErrors({});
    setFormError(null);
    setStage('credentials');
  }

  function finishSignIn(): void {
    navigate(nextPath ?? ROUTES.postAuth, { replace: true });
  }

  async function lookupTeam(candidate: string): Promise<void> {
    const normalised = candidate.trim().toLowerCase();
    const slugError = validateSlug(normalised);
    if (slugError !== null) {
      setFieldErrors({ slug: slugError });
      return;
    }
    setFieldErrors({});
    setFormError(null);
    setIsBusy(true);
    try {
      applyTeam(await apiClient.lookupTeam(normalised));
    } catch (error) {
      setFieldErrors({ slug: toTeamLookupMessage(error, normalised) });
    } finally {
      setIsBusy(false);
    }
  }

  async function onLookupTeam(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await lookupTeam(slug);
  }

  /** Explicit selection of the team last used on this device. */
  async function onUseRememberedTeam(): Promise<void> {
    setSlug(rememberedSlug);
    await lookupTeam(rememberedSlug);
  }

  async function onSubmitCredentials(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (team === null) return;
    const emailError = validateEmail(email, 'staff');
    const errors: Record<string, string> = {};
    if (emailError !== null) errors.email = emailError;
    if (password === '') errors.password = 'Enter your password.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setFormError(null);
    setIsBusy(true);
    try {
      const tokens = await apiClient.login({
        tenantId: team.tenant_id,
        email: email.trim(),
        password,
      });
      startSession(tokens, team.slug);
      setPassword('');
      if (tokens.mfa_required) {
        setIsEnrolled(tokens.mfa_enrolled);
        setStage('mfa');
        return;
      }
      finishSignIn();
    } catch (error) {
      setFormError(toLoginMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  function onUseAnotherTeam(): void {
    setTeam(null);
    setSlug('');
    setStage('team');
    setFormError(null);
    setFieldErrors({});
  }

  return (
    <div className="auth">
      <Link to={ROUTES.landing} className="auth__brand">
        <img src="/images/logo-mark.png" alt="" width={32} height={32} />
        <span>
          GRIDIRON <em>TRACKER</em>
        </span>
      </Link>

      <main className="auth__panel">
        {formError ? (
          <div className="banner banner--error" role="alert">
            <span className="banner__title">Sign-in failed</span>
            <span>{formError}</span>
          </div>
        ) : null}

        {stage === 'resolving' ? (
          <div className="stack" aria-busy="true">
            <h1 className="auth__title">Sign in</h1>
            <p className="muted" role="status">
              Finding team <span className="mono">{linkedSlug}</span>…
            </p>
          </div>
        ) : null}

        {stage === 'team' ? (
          <form className="stack" onSubmit={(event) => void onLookupTeam(event)} noValidate>
            <div>
              <h1 className="auth__title">Sign in to your team portal</h1>
              <p className="muted">
                Enter your team code — the short name in your portal address, e.g.
                <span className="mono"> westview-eagles</span>.
              </p>
            </div>
            <TextField
              label="Team code"
              name="team_slug"
              required
              autoFocus
              spellCheck={false}
              value={slug}
              error={fieldErrors.slug ?? null}
              onChange={(event) => setSlug(event.target.value.toLowerCase())}
            />
            <button type="submit" className="btn btn--primary btn--block" disabled={isBusy}>
              {isBusy ? 'Finding team…' : 'Find my team'}
            </button>
            {rememberedSlug !== '' ? (
              <p className="auth__remembered">
                Last used on this device:{' '}
                <button
                  type="button"
                  className="auth__link-button mono"
                  disabled={isBusy}
                  onClick={() => void onUseRememberedTeam()}
                >
                  {rememberedSlug}
                </button>
              </p>
            ) : null}
          </form>
        ) : null}

        {stage === 'credentials' && team !== null ? (
          <>
            <h1 className="auth__title">Sign in</h1>
            <TeamChip team={team} onSwitch={onUseAnotherTeam} />
            <form
              className="stack"
              onSubmit={(event) => void onSubmitCredentials(event)}
              noValidate
            >
              <TextField
                label="Email"
                name="username"
                type="email"
                required
                autoFocus
                autoComplete="username"
                value={email}
                error={fieldErrors.email ?? null}
                onChange={(event) => setEmail(event.target.value)}
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                error={fieldErrors.password ?? null}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button type="submit" className="btn btn--primary btn--block" disabled={isBusy}>
                {isBusy ? 'Signing in…' : 'Sign in'}
              </button>
              <p className="field__hint">
                <Link to={`${ROUTES.forgotPassword}?team=${encodeURIComponent(team.slug)}`}>
                  Forgot your password?
                </Link>
              </p>
            </form>
            <p className="auth__divider">or</p>
            <div className="sso-list">
              {SSO_PROVIDERS.map((provider) => (
                <a
                  key={provider.id}
                  className="btn btn--secondary btn--block"
                  href={apiClient.oauthAuthorizeUrl(
                    provider.id,
                    team.tenant_id,
                    `${globalThis.location?.origin ?? ''}${ROUTES.oauthCallback}`,
                    team.slug
                  )}
                >
                  {provider.label}
                </a>
              ))}
              <p className="field__hint">
                Single sign-on works only if your Team Admin has configured that provider for{' '}
                {team.name}.
              </p>
            </div>
          </>
        ) : null}

        {stage === 'mfa' ? (
          <>
            {team !== null ? <TeamChip team={team} /> : null}
            <LoginMfaStep
              isEnrolled={isEnrolled}
              onVerified={finishSignIn}
              onCancel={onUseAnotherTeam}
            />
          </>
        ) : null}
      </main>

      <p className="auth__footnote">
        New program? <Link to={ROUTES.signup}>Start a 30-day trial</Link>
      </p>
    </div>
  );
}

/** Turn an auth failure into copy a coach can act on. */
function toLoginMessage(error: unknown): string {
  if (!ApiError.isApiError(error)) return 'Sign-in failed. Please try again.';
  if (error.isOffline) return error.message;
  switch (error.status) {
    case 401:
      return 'That email and password do not match an account in this team portal.';
    case 423:
      return 'This account is temporarily locked after repeated failed attempts. Try again later, use “Forgot your password?” below, or ask your Team Admin to reset it.';
    case 403:
      return 'This account cannot sign in to this team portal. Check the team code with your Team Admin.';
    default:
      return toUserMessage(error, 'Sign-in failed. Please try again.');
  }
}
